import * as React from "react";
import { useParams } from "react-router";

import { Container, Grid } from "@mui/material";

import TextFieldsIcon from "@mui/icons-material/TextFields";

import CustomForm from "../../../helper-components/form/CustomForm.jsx";
import NavigationFooter from "../../../helper-components/misc/NavigationFooter.jsx";
import PaperText from "../../../helper-components/misc/PaperText.jsx";

import ExaminationCreate from "./ExaminationCreate.jsx";

import { isEmpty } from "../../../helper-functions/shared/miscUtils.js";
import { queryExamination } from "../../../helper-functions/api/examinationQueries.js";
import { useExamination } from "../../../helper-functions/context/ExaminationContext.jsx";

import { normalizeSummaryData, normalizeTeethData } from "../../../helper-functions/shared/teethDataNormalize.js";
import { patientOwnerKey } from "../../../helper-functions/shared/examinationDraft.js";

import { getLanguage } from "../../../config/language/language.js";

/**
 * A page for editing an existing examination.
 *
 * Loads the examination data into state and renders ExaminationCreate in edit mode.
 */
export default function ExaminationEdit(props) {
    const { id } = useParams(); // Get the URL parameters from react-router.

    const examinations = props?.backend?.examinations?.data;
    const { setChartingTable, setPatient, setSummaryData, setDraftOwnerKey } = useExamination();

    const [examination, setExamination] = React.useState({});
    const [examinationQuery, setExaminationQuery] = React.useState(false);
    const [dataLoaded, setDataLoaded] = React.useState(false);

    const isReady = !isEmpty(examination) && !isEmpty(examination?.teeth_data);

    // Set the examination, and potentially query it if it's not available.
    React.useEffect(() => {
        const possibleExamination = examinations.find((t) => t.id === parseInt(id));

        if (!isEmpty(possibleExamination)) {
            setExaminationQuery(true);
            setExamination(possibleExamination);
        }

        if (isEmpty(possibleExamination) && !examinationQuery) {
            setExaminationQuery(true);
            queryExamination(props, { id });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [examinations, examination]);

    // Load the examination data into the app state when ready
    React.useEffect(() => {
        if (isReady && !dataLoaded) {
            // Set the charting table state with the examination's teeth data
            setChartingTable(normalizeTeethData(examination.teeth_data));
            setSummaryData(normalizeSummaryData(examination.summary_data));
            setPatient(examination.patient);
            setDraftOwnerKey(patientOwnerKey(examination.patient));

            setDataLoaded(true);

            console.log("Loaded examination data for editing:", examination.id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isReady, dataLoaded]);

    // Loading state
    if (!examinationQuery || !isReady) {
        return (
            <Container maxWidth="xl" sx={{ mt: 2, mb: 2 }}>
                <Grid container spacing={1}>
                    <Grid item xs={12}>
                        <CustomForm title={getLanguage().EXAMINATION_EDIT_TITLE} subtitle={getLanguage().EXAMINATION_EDIT_SUBTITLE} icon={<TextFieldsIcon />} />
                    </Grid>

                    <Grid item xs={12}>
                        {!examinationQuery && <PaperText value="Loading, please wait..." center p={8} />}
                        {examinationQuery && !isReady && <PaperText value={getLanguage().NO_EXAMINATION_FOUND} />}
                    </Grid>

                    <NavigationFooter
                        rows={1}
                        options={[
                            {
                                path: `/database/examinations`,
                                title: "Back to examinations",
                                variant: "outlined",
                                icon: <TextFieldsIcon />
                            }
                        ]}
                    />
                </Grid>
            </Container>
        );
    }

    // Render ExaminationCreate in edit mode once data is loaded
    if (!dataLoaded) {
        return (
            <Container maxWidth="xl" sx={{ mt: 2, mb: 2 }}>
                <Grid container spacing={1}>
                    <Grid item xs={12}>
                        <PaperText value="Preparing examination for editing..." center p={8} />
                    </Grid>
                </Grid>
            </Container>
        );
    }

    return <ExaminationCreate {...props} mode="edit" examinationId={parseInt(id)} />;
}
